// Northwestern University: public college page (#2743), official facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - Northwestern's Common Data Set 2025-2026 (2025-2026.pdf), linked from
//    University Enrollment's Common Data Set page: C1 applicants, admits and
//    enrollees for Fall 2025 (the C1 residency table is not filled in), C8
//    test policy for Fall 2027, C21/C22 plans and the Fall 2025 early
//    decision counts, G1/G5 2026-2027 costs.
//  - admissions.northwestern.edu: Application Dates and Deadlines (the
//    deadline table for fall 2027 entry, the Early Decision agreement) and
//    the Standardized Testing Policy FAQ (test-optional, superscoring, the
//    Integrated Science and MMSS programs' separate applications).
//  - Bienen School of Music, Undergraduate Application and Timeline (Fall
//    2027 first-year timelines).
//  - College Scorecard, school id 147767 (net price by family income), read
//    from the Scorecard API on 2026-10-01.
// Cost: the CDS G1/G5 items. G5 gives transportation as "varies", so no
// transportation row is shown and the total leaves it out. The financial
// aid office's cost of attendance page lists the same items plus $54 in loan
// fees (its total, $99,375, includes them).
// No fall 2028 change was announced on the admissions pages on 2026-10-01.
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'Northwestern Common Data Set 2025-2026',
        url: 'https://enrollment.northwestern.edu/data/2025-2026.pdf',
        read: '2026-10-01',
    },
    deadlines: {
        label: 'Northwestern Admissions, Application Dates and Deadlines',
        url: 'https://admissions.northwestern.edu/apply/application-deadlines.html',
        read: '2026-10-01',
    },
    tests: {
        label: 'Northwestern Admissions, Standardized Testing Policy',
        url: 'https://admissions.northwestern.edu/faqs/standardized-testing-policy/index.html',
        read: '2026-10-01',
    },
    bienen: {
        label: 'Bienen School of Music, Undergraduate Application and Timeline',
        url: 'https://music.northwestern.edu/admission/undergraduate/application-timeline',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?147767-Northwestern-University',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'northwestern-university',
    kbId: 'northwestern_university',
    name: 'Northwestern University',
    shortName: 'Northwestern',
    aliases: ['NU'],
    location: 'Evanston, IL',
    control: 'Private',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (Northwestern brand color palettes); decoration only.
    branding: {
        primary: '#4E2A84',
        secondary: '#FFFFFF',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/northwestern_university_logo.png',
    },

    // The direct answer and short FAQ (#4229): every figure restates one the
    // page already shows, from the source named beside it.
    // The search it answers: northwestern application deadline.
    faq: {
        answer: { text: 'For fall 2027 entry, Northwestern\'s Early Decision deadline is November 1, with decisions in mid-December, and its Regular Decision deadline is January 4, with decisions in late March.', source: 'deadlines' },
        questions: [
            { q: 'Is Northwestern Early Decision binding?', a: 'Yes. If you are admitted Early Decision, you agree to withdraw all your applications at other colleges and enroll at Northwestern, and you submit an Early Decision agreement with your application. Early Decision admits confirm by February 1.', source: 'deadlines' },
            { q: 'What is the Northwestern acceptance rate?', a: 'For fall 2025 entry, Northwestern admitted 3,933 of 52,676 first-year applicants, 7.5%. In its one Early Decision round it admitted 1,212 of 5,939, or 20.4%.', source: 'cds', where: 'sections C1 and C21' },
            { q: 'Does Northwestern superscore?', a: 'Northwestern superscores the SAT. For the ACT, you report your highest section scores and your official ACT superscore. Testing is optional, and applying without scores does not affect your review.', source: 'tests' },
            { q: 'How much does it cost to apply to Northwestern?', a: 'The application fee is $75, or you can apply with a fee waiver.', source: 'deadlines' },
            { q: 'How many first-year students enrolled at Northwestern in fall 2025?', a: 'Northwestern enrolled 2,139 first-year students in fall 2025, out of 3,933 admitted, according to its Common Data Set 2025-2026.', source: 'cds', where: 'section C1' },
        ],
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 52676,
        admitted: 3933,
        enrolled: 2139,
        source: 'cds',
        where: 'section C1',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'Northwestern\'s Common Data Set reports one first-year acceptance rate for the whole university, with no rate by school or major.',
        source: 'cds',
    },
    earlyRounds: {
        title: 'Early decision',
        note: 'Northwestern has one early decision plan and no early action plan. These counts are part of the totals above.',
        year: 'Fall 2025',
        rows: [
            { plan: 'Early Decision', applied: 5939, admitted: 1212 },
        ],
        source: 'cds',
        where: 'sections C21 and C22',
    },

    programs: {
        entering: 'Fall 2027',
        intro: 'You apply to Northwestern and name the school you want to enter. Some programs ask for more:',
        rows: [
            { school: 'Bienen School of Music', requirement: 'Every Bienen applicant, whatever the major, completes the Bienen School Supplement (prescreening where it applies, BA in Music materials or a composition portfolio), due October 15 for Early Decision and December 1 for Regular Decision. Early Decision auditions must be done by November 23; Regular Decision auditions are on campus February 5 to 14.', source: 'bienen' },
            { school: 'Integrated Science Program and MMSS', requirement: 'These two selective programs have their own application and admission process beyond general admission to Northwestern. Check each program\'s site for its current requirements.', source: 'tests' },
        ],
        source: 'deadlines',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Regular Decision', value: 'January 4' },
        rows: [
            { plan: 'Early Decision', deadline: 'November 1', decision: 'Mid-December', binding: true },
            { plan: 'Regular Decision', deadline: 'January 4', decision: 'Late March', binding: false },
        ],
        note: 'Applications are due by 11:59 pm in your local time. Financial aid materials are due December 1, 2026 for Early Decision and February 1, 2027 for Regular Decision. QuestBridge applicants apply by October 1, 2026. The application fee is $75, or a fee waiver. Early Decision admits confirm by February 1; Regular Decision admits by May 1.',
        source: 'deadlines',
        binding: 'Early Decision is binding: as with all traditional early decision plans, you agree to withdraw all applications at other colleges and enroll at Northwestern if admitted. You submit an Early Decision agreement with your application.',
        bindingSource: 'deadlines',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'Northwestern is test-optional and does not require the SAT or ACT. If you report scores, they are weighed with the rest of your application; applying without them does not affect your review. You may self-report, and Northwestern superscores the SAT. For the ACT, report your highest section scores and your official ACT superscore. Early Decision applicants may self-report October or November scores by November 30, 2026; Regular Decision applicants, December scores by February 15, 2027.',
        source: 'tests',
        cdsLine: 'The Common Data Set also marks the SAT or ACT as not required for fall 2027 applicants, but considered if submitted.',
        cdsWhere: 'section C8',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'tests',
    },

    cost: {
        year: '2026-2027',
        rows: [
            { item: 'Tuition', amount: 71802 },
            { item: 'Required fees', amount: 1260 },
            { item: 'Food and housing (on campus)', amount: 22941 },
            { item: 'Books and supplies', amount: 1239 },
            { item: 'Other expenses', amount: 2079 },
        ],
        total: 99321,
        note: 'For a full-time undergraduate living on campus, fall, winter and spring quarters. Northwestern lists transportation as "varies", so it is not included.',
        source: 'cds',
        where: 'sections G1 and G5',
        netPrice: {
            average: 34420,
            byIncome: [
                { income: '$0 to $30,000', amount: 1427 },
                { income: '$30,001 to $48,000', amount: 7536 },
                { income: '$48,001 to $75,000', amount: 8811 },
                { income: '$75,001 to $110,000', amount: 18954 },
                { income: 'Over $110,000', amount: 56074 },
            ],
            note: 'What first-year, full-time students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from Northwestern\'s aid offer.',
            tileDetail: 'Per year, after grants, federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
